import { Modal, FormControlLabel, Checkbox, Button, CircularProgress } from "@mui/material";
import styled from "styled-components";
import { useEffect, useState } from "react";
import Api from "../libs/api";
import Storage from "../libs/storage";

const ModalCenter = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    max-width: 632px;
    background-color: #ffffff;
    padding: 40px;
    border-radius: 8px;
    border: 1px solid rgb(144, 144, 144);
    -webkit-box-shadow: 0px 0px 20px 0px rgba(0, 0, 0, 0.75);
    -moz-box-shadow: 0px 0px 20px 0px rgba(0, 0, 0, 0.75);
    box-shadow: 0px 0px 20px 0px rgba(0, 0, 0, 0.75);

    @media (max-width: 800px) {
        margin-right: 16px;
        width: calc(100% - 32px);
    }
`;

const CustomizedTitle = styled.div`
    color: #000;
    font-size: 24px;
    font-style: normal;
    font-weight: 600;
    line-height: 150%;
    letter-spacing: -0.96px;
    margin-bottom: 30px;
`;

const Line = styled.div`
  border: none;
  height: 10px;
  border-bottom: 2px solid #57c4c2;
  width: 96px;
`;

const TitleBreak = styled.br`
    display: none;

    @media (max-width: 800px) {
        display: block;
    }
`;

const ModalContent = styled.div`
    display: flex;
    padding: 40px;
`;

const Content = styled.div`
    flex: 1;
`;

const Text = styled.p`
    color: #181122;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 133%;
    letter-spacing: -0.28px;
    margin-bottom: 24px;
`;

const Box = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    border-radius: 8px;
    border: 1px solid #E3E3E3;
    margin-bottom: 24px;
    width: 100%;
`;

const BoxLoading = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0px;
    width: 100%;
    margin-bottom: 24px;
    height: 85px;
    border-radius: 8px;
    border: 1px solid #E3E3E3;
`;

const TextBox = styled.p`
    color: #000;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 150%;
`;

const SubTitleBox = styled.p`
    color: '#999';
    font-size: 11px;
    font-style: normal;
    font-weight: 500;
    line-height: 150%;
`;

const TermsLabel = styled.span`
    color: #000;
    font-family: Montserrat;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
`;

const TermsLink = styled.a`
    color: #000;
    font-family: Montserrat;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-skip-ink: none;
    text-decoration-thickness: auto;
    text-underline-offset: auto;
    text-underline-position: from-font;
`;

const ModalButtonsRow = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    gap: 16px;
    margin-top: 24px;
`;

const PrimaryButton = styled(Button)`
    && {
        background: #57c4c2;
        color: #000;
        font-family: Montserrat;
        font-size: 14px;
        font-weight: 600;
        text-transform: none;
        padding: 12px 24px;
        border-radius: 8px;
        text-transform: uppercase;
        width: 100%;

        &:hover {
            background: #4ba09b;
        }

        &:disabled {
            background: #e4e4e4;
            color: #999;
        }
    }
`;

const LinkButton = styled(Button)`
    && {
        color: #419392;
        font-size: 14px;
        font-weight: 600;
        text-transform: uppercase;
        padding: 0px;
        line-height: 20px;
    }
`;

const BoxHighlightedText = styled.div`
    display: flex;
    align-items: center;
`;

const HighlightedTextCard = styled.p`
    color: #000;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: 150%;
`;

const ModalAcceptTerms = ({
    showModal,
    setShowModal,
    activeUsersCount,
    acceptedTerms,
    setAcceptedTerms,
}) => {
    const api = Api();
    const storage = Storage();
    const [suggestedPlan, setSuggestedPlan] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const getPlans = async () => {
            const data = await storage.getItem("data");
            if (!data) return;
            try {
                let response = await api.getAvailablePlans(data.token);
                if (response.data?.availablePlans) {
                    const first5Plans = response.data.availablePlans.slice(1, 6);
                    const formattedPlans = first5Plans.map(plan => ({
                        id: plan.id,
                        name: plan.name,
                        price: plan.price,
                        maxUsers: plan.maxUsers,
                        description: plan.description,
                        hasLink: false,
                    }));

                    formattedPlans.push({
                        id: "custom",
                        name: "+ de 100 usuários",
                        price: "Sob consulta",
                        maxUsers: null,
                        description: "Sob consulta",
                        hasLink: true,
                    });

                    const suggested = formattedPlans.find(plan =>
                        plan.maxUsers && activeUsersCount <= plan.maxUsers
                    );

                    setSuggestedPlan(suggested || formattedPlans[0]);
                }
            } catch (error) {
                console.error("Erro ao buscar planos:", error);
            }
        };

        if (showModal && activeUsersCount) {
            getPlans();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showModal, activeUsersCount]);

    const getPriceFormatted = () => {
        if (!suggestedPlan) return { priceInt: '0', priceDec: '00' };
        const priceStr = String(suggestedPlan.price);
        const priceInt = priceStr.split('.')[0];
        const priceDec = (priceStr.split('.')[1] || '00').padEnd(2, '0');
        return { priceInt, priceDec };
    };

    const { priceInt, priceDec } = getPriceFormatted();

    const handleAcceptTerms = async () => {
        setIsLoading(true);
        try {
            const data = await storage.getItem("data");
            if (!data) return;

            const response = await api.acceptTermsOfUse(data.token);

            if (response.data?.acceptTermsOfUse?.success) {
                setShowModal(false);
            } else {
                console.error("Erro ao aceitar termos:", response.data?.acceptTermsOfUse?.message);
            }
        } catch (error) {
            console.error("Erro ao aceitar termos:", error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal
            open={showModal}
        >
            <ModalCenter style={{ overflowY: "auto", maxHeight: "95%", padding: 0, border: 0 }}>
                <ModalContent>
                    <Content>
                        <CustomizedTitle>Seu plano para 2026<TitleBreak /> foi atualizado! <Line /></CustomizedTitle>
                        <Text>
                            A política de preços do Zag para 2026 foram atualizados.
                            <br />
                            Confira seu novo plano e aceite os termos de uso para prosseguir:
                        </Text>
                        {suggestedPlan ? (
                            <Box>
                                <TextBox>{suggestedPlan.description}</TextBox>
                                <div>
                                    <BoxHighlightedText>
                                        <HighlightedTextCard style={{ fontSize: 12, paddingRight: 2 }}>R$</HighlightedTextCard>
                                        <HighlightedTextCard style={{ fontSize: 24 }}>{priceInt}</HighlightedTextCard>
                                        <HighlightedTextCard style={{ fontSize: 16 }}>,{priceDec}</HighlightedTextCard>
                                    </BoxHighlightedText>
                                    <SubTitleBox>/por mês</SubTitleBox>
                                </div>
                            </Box>
                        ) : (
                            <BoxLoading><CircularProgress size={20} sx={{ '& circle': { stroke: '#000' } }} /></BoxLoading>
                        )}
                        <FormControlLabel
                            style={{ marginLeft: 0 }}
                            control={
                                <Checkbox
                                    sx={{
                                        p: 0,
                                        ml: 0,
                                        mr: 1,
                                    }}
                                    checked={acceptedTerms}
                                    onChange={(e) =>
                                        setAcceptedTerms(
                                            e.target.checked
                                        )
                                    }
                                />
                            }
                            label={
                                <TermsLabel>
                                    Li e aceito os{" "}
                                    <TermsLink
                                        href="https://zagapp.com.br/termos-de-uso"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        termos de uso
                                    </TermsLink>
                                    .
                                </TermsLabel>
                            }
                        />
                        <ModalButtonsRow>
                            <span style={{ cursor: !acceptedTerms || isLoading ? 'not-allowed' : 'pointer', width: '100%' }}>
                                <PrimaryButton
                                    disabled={!acceptedTerms || isLoading}
                                    onClick={handleAcceptTerms}
                                >
                                    {isLoading ? <CircularProgress size={20} sx={{ '& circle': { stroke: '#000' } }} /> : 'aceitar informações'}
                                </PrimaryButton>
                            </span>
                            <LinkButton
                                onClick={() => window.location.href="mailto:oi@zagapp.com.br"}
                            >
                                em caso de dúvidas, fale com o suporte.
                            </LinkButton>
                        </ModalButtonsRow>
                    </Content>
                </ModalContent>
            </ModalCenter>
        </Modal>
    );
}

export default ModalAcceptTerms;
